import { useCallback, useEffect, useRef, useState } from 'react';
import { showError, showInfo, showSuccess } from '../../../shared/ui/appToast';
import type { Dispatch, SetStateAction } from 'react';
import type { CategoryRecord } from '../../../catalog/domain-api'
import type { Product } from '../../../catalog/domain-api';
import { normalizeProductCategoryId } from '../../../catalog/domain-api';
import { CatalogPlanLimitExceededError, getCatalogPlanLimitExceededMessage } from '../../../catalog/app-api'
import { findProductByCode } from '../../../catalog/infra-api'
import { importProductFromRemoteSuggestion, ImportProductFromRemoteValidationError } from '../../../catalog/app-api'
import type { RemoteBarcodeHit } from '../../../catalog/domain-api';
import type { PosBarcodeImportConfirmPayload } from '../../components/modals/PosBarcodeImportModal';
import { DATABASE_UNAVAILABLE_USER_MESSAGE, isDatabaseUnavailableError } from '../../../shared/localDb';
import type { PlanEntitlementsRow } from '../../../shell/feature-flags/domain/planEntitlements';
import { loadPosCategories } from './posCategoryLoader';
import { useHasBusinessCapability } from '../../../shell/session/hooks/useEffectiveBusinessCapabilities';

export function usePosRemoteBarcodeImport(options: {
  canImportFromCatalog: boolean;
  currentBranchId: string | null | undefined;
  entitlements: PlanEntitlementsRow | null | undefined;
  userId: string | null | undefined;
  categories: CategoryRecord[];
  setCategories: Dispatch<SetStateAction<CategoryRecord[]>>;
  setCategory: (c: Product['categoryId'] | 'all') => void;
  setSearchQuery: (q: string) => void;
  setLookupFeedback: (s: string | null) => void;
  handleProductPress: (product: Product) => void;
  reloadCatalog: () => void | Promise<void>;
}) {
  const {
    canImportFromCatalog,
    currentBranchId,
    entitlements,
    userId,
    setCategories,
    setCategory,
    setSearchQuery,
    setLookupFeedback,
    handleProductPress,
    reloadCatalog,
  } = options;

  const inventoryStockUxEnabled = useHasBusinessCapability('inventory');
  const [remoteImportHit, setRemoteImportHit] = useState<RemoteBarcodeHit | null>(null);
  const [remoteImportVisible, setRemoteImportVisible] = useState(false);
  const [remoteImportSaving, setRemoteImportSaving] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const handleRemoteBarcodeHit = useCallback(
    (hit: RemoteBarcodeHit) => {
      setRemoteImportHit(hit);
      setImportError(null);
      setRemoteImportVisible(true);
      void loadPosCategories(setCategories, () => true, { forceRemoteSync: true });
    },
    [setCategories]
  );

  const closeRemoteImport = useCallback(() => {
    if (remoteImportSaving) return;
    setRemoteImportVisible(false);
    setRemoteImportHit(null);
    setImportError(null);
  }, [remoteImportSaving]);

  const handleConfirmBarcodeImport = useCallback(
    async (payload: PosBarcodeImportConfirmPayload) => {
      if (!remoteImportHit || !canImportFromCatalog) return;
      setRemoteImportSaving(true);
      setImportError(null);
      try {
        await importProductFromRemoteSuggestion({
          remoteHit: remoteImportHit,
          price: payload.price,
          name: payload.name,
          categoryId: payload.categoryId,
          planEnt: entitlements,
          performedByUserId: userId ?? null,
          initialBranchQuantity: inventoryStockUxEnabled ? 1 : 0,
        });
        const row = await findProductByCode({
          code: remoteImportHit.barcode,
          branchId: currentBranchId ?? null,
        });
        if (!row) {
          showSuccess(
            'Producto importado',
            'Se guardó. Buscá el código en la grilla para agregarlo al carrito.'
          );
        } else {
          setCategory(normalizeProductCategoryId(row.categoryId));
          setSearchQuery(row.sku);
          handleProductPress(row);
          setLookupFeedback(`Importado: ${row.name}`);
        }
        await reloadCatalog();
        if (cancelledRef.current) return;
        setRemoteImportVisible(false);
        setRemoteImportHit(null);
      } catch (error: unknown) {
        if (error instanceof CatalogPlanLimitExceededError) {
          // Plan limit: no hay acción recuperable desde el modal — cerrar y notificar.
          const { title, message } = getCatalogPlanLimitExceededMessage(error);
          showInfo(title, message);
          setRemoteImportVisible(false);
          setRemoteImportHit(null);
        } else if (error instanceof ImportProductFromRemoteValidationError) {
          // Error de validación del backend (SKU/barcode duplicado, etc.) — el usuario
          // puede corregir desde el modal. Mostrar error inline.
          setImportError(error.message);
        } else if (isDatabaseUnavailableError(error)) {
          setImportError(DATABASE_UNAVAILABLE_USER_MESSAGE);
        } else {
          const message = error instanceof Error ? error.message : 'No se pudo importar el producto.';
          setImportError(message);
        }
      } finally {
        setRemoteImportSaving(false);
      }
    },
    [
      canImportFromCatalog,
      currentBranchId,
      entitlements,
      handleProductPress,
      remoteImportHit,
      reloadCatalog,
      setCategory,
      setLookupFeedback,
      setSearchQuery,
      userId,
      inventoryStockUxEnabled,
    ]
  );

  const defaultImportCategoryId = null;

  return {
    remoteImportHit,
    remoteImportVisible,
    remoteImportSaving,
    importError,
    defaultImportCategoryId,
    handleRemoteBarcodeHit,
    closeRemoteImport,
    handleConfirmBarcodeImport,
  };
}
